import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { TermEditor } from '@/components/terms/term-editor';
import { TermPreviewDialog } from '@/components/terms/term-preview-dialog';
import { StatusPill } from '@/components/ui/admin/dashboard';
import { Field } from '@/components/ui/admin/field';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { CopyText } from '@/components/ui/copy-text';
import { useRouter } from '@/hooks/use-router';
import { date, localeFormat } from '@/lib/date';
import { meta } from '@/lib/meta';
import { TERM_KIND_LABELS, TERM_STATUS_LABELS } from '@/lib/terms/variables';
import { usePermissions } from '@/services/auth';
import {
  useDeleteTermVersion,
  useNewDraftTermVersion,
  usePublishTermVersion,
  useSaveTermDraft,
  useTermVersion,
} from '@/services/term-versions';

export const Route = createFileRoute('/_app/term-versions/$id')({
  head: () => meta({ label: 'Termo' }),
  component: RouteComponent,
});

const model = 'term-versions';

type Kind = keyof typeof TERM_KIND_LABELS;
type Status = keyof typeof TERM_STATUS_LABELS;
type TermDocument = Record<string, unknown>;

function RouteComponent() {
  const { id } = Route.useParams();
  const { push } = useRouter();
  const { createPermissions } = usePermissions();
  const { canUpdate, canDelete } = createPermissions(model);

  const { data, isLoading } = useTermVersion({ id } as any);
  const version = data as any;

  const { mutateAsync: save, isPending: isSaving } = useSaveTermDraft();
  const { mutateAsync: publish, isPending: isPublishing } = usePublishTermVersion();
  const { mutateAsync: newDraft, isPending: isDrafting } = useNewDraftTermVersion();
  const { mutateAsync: remove, isPending: isRemoving } = useDeleteTermVersion();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState<TermDocument | null>(null);
  const [dirty, setDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirm, setConfirm] = useState<'publish' | 'delete' | null>(null);

  // Carrega o estado local a cada versão aberta (trocar de rascunho pra publicada, etc.).
  useEffect(() => {
    if (!version) return;
    setTitle(version.title);
    setContent(version.content);
    setDirty(false);
  }, [version?.id, version?.status]);

  if (!version || !content) {
    return <Content isLoading={isLoading} breadcrumbs={{ title: 'Termo', items: [] }} />;
  }

  const isDraft = version.status === 'draft';
  const editable = isDraft && canUpdate;
  const kindLabel = TERM_KIND_LABELS[version.kind as Kind];

  const saveDraft = async () => {
    await save({ id, data: { title, content } });
    setDirty(false);
  };

  const handleSave = async () => {
    await saveDraft();
  };

  const handlePublish = async () => {
    setConfirm(null);
    if (dirty) await saveDraft();
    await publish(id);
  };

  const handleDelete = async () => {
    setConfirm(null);
    await remove({ id } as any);
    push(`/${model}`);
  };

  const handleNewDraft = async () => {
    const created: any = await newDraft(id);
    if (created?.id) push(`/${model}/${created.id}`);
  };

  return (
    <Content
      isLoading={isLoading || isSaving || isPublishing || isDrafting || isRemoving}
      breadcrumbs={{
        icon: 'scroll-text',
        title: `${kindLabel} — v${version.version}`,
        items: [
          { label: 'Termos', route: `/${model}` },
          { label: isDraft ? 'Editar rascunho' : 'Visualizar', active: true },
        ],
      }}
    >
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5">
        <section className="overflow-hidden rounded-[16px] border border-border bg-card">
          <div className="flex flex-wrap items-center gap-3 border-b border-border px-[26px] py-5">
            <span className="mr-auto flex min-w-0 flex-col gap-1.5">
              <span className="flex flex-wrap items-center gap-2.5">
                <span className="font-display text-[18px] font-extrabold uppercase tracking-tight text-white">
                  Versão {version.version}
                </span>
                <StatusPill
                  tone={
                    version.status === 'published'
                      ? 'olive'
                      : version.status === 'draft'
                        ? 'sand'
                        : 'muted'
                  }
                >
                  {TERM_STATUS_LABELS[version.status as Status]}
                </StatusPill>
              </span>
              <span className="text-[13px] text-muted-foreground">
                {isDraft
                  ? 'Rascunho: edite à vontade. Depois de publicado, o texto não muda mais — vira prova de aceite.'
                  : `Publicada em ${date(version.publishedAt).format(localeFormat)}. Somente leitura: é o texto que os recrutas aceitaram.`}
              </span>
            </span>
            {!isDraft && version.contentHash && (
              <span className="flex items-center gap-2 text-[12px] text-faint">
                <span className="col-label">Hash</span>
                <CopyText
                  value={version.contentHash}
                  label={`${version.contentHash.slice(0, 12)}…`}
                />
              </span>
            )}
          </div>

          <div className="grid gap-4 p-[26px]">
            <Field
              label="Título"
              required
              value={title}
              readOnly={!editable}
              placeholder="Ex: Termo de participação"
              onChange={(event) => {
                setTitle(event.target.value);
                setDirty(true);
              }}
            />
            <TermEditor
              key={`${version.id}-${version.status}`}
              content={content}
              readOnly={!editable}
              onChange={(next) => {
                setContent(next);
                setDirty(true);
              }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2.5 border-t border-border bg-sunken px-[26px] py-[18px]">
            {editable && canDelete && (
              <Button
                variant="destructive"
                size="md"
                icon="trash-2"
                onClick={() => setConfirm('delete')}
              >
                Excluir rascunho
              </Button>
            )}
            {dirty && (
              <span className="text-[12.5px] text-sand">Alterações não salvas</span>
            )}

            <span className="ml-auto flex flex-wrap items-center gap-2.5">
              <Button
                variant="outline"
                size="md"
                icon="eye"
                onClick={() => setPreviewOpen(true)}
              >
                Pré-visualizar
              </Button>
              {editable ? (
                <>
                  <Button
                    variant="outline"
                    size="md"
                    icon="save"
                    disabled={!dirty}
                    onClick={handleSave}
                  >
                    Salvar rascunho
                  </Button>
                  <Button
                    variant="sand"
                    size="lg"
                    icon="send"
                    onClick={() => setConfirm('publish')}
                  >
                    Publicar versão
                  </Button>
                </>
              ) : (
                canUpdate && (
                  <Button
                    variant="sand"
                    size="lg"
                    icon="file-plus-2"
                    onClick={handleNewDraft}
                  >
                    Nova versão a partir desta
                  </Button>
                )
              )}
            </span>
          </div>
        </section>
      </div>

      <TermPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={title}
        content={content}
      />

      <AlertDialog
        open={confirm !== null}
        onOpenChange={(open) => !open && setConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === 'publish'
                ? `Publicar a versão ${version.version}?`
                : 'Excluir rascunho?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'publish'
                ? 'Ela passa a ser o termo vigente e a atual vai para o histórico. Depois de publicada, o texto não pode mais ser alterado.'
                : 'O rascunho sai da lista. Versões publicadas não são afetadas.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirm === 'publish' ? handlePublish : handleDelete}
            >
              {confirm === 'publish' ? 'Publicar' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Content>
  );
}
